// Central app-session + API client. On app open the server issues a signed session (token + id);
// every DB call (projects, events, profile, admin) must present it, or the server rejects it.
import { getClientId } from "./projects";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "dsymbia_session";

let _session = null;          // { session_id, token }
let _initPromise = null;

function loadCached() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export async function ensureSession() {
  if (_session) return _session;
  const cached = loadCached();
  if (cached?.session_id && cached?.token) { _session = cached; return _session; }
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    const res = await fetch(`${BASE}/api/session/init`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Client-Id": getClientId() },
      body: JSON.stringify({ client_id: getClientId() }),
    });
    const data = await res.json();
    _session = { session_id: data.session_id, token: data.token };
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(_session)); } catch { /* ignore */ }
    return _session;
  })();
  return _initPromise;
}

export async function sessionHeaders(extra = {}) {
  const s = await ensureSession();
  return {
    "X-Client-Id": getClientId(),
    "X-Session-Id": s.session_id,
    "X-Session-Token": s.token,
    ...extra,
  };
}

// Generic authenticated API call (JSON in/out). credentials:'include' carries auth cookies.
export async function apiFetch(path, { method = "GET", body, headers = {} } = {}) {
  const h = await sessionHeaders(headers);
  if (body !== undefined) h["Content-Type"] = "application/json";
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: h,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}

// Fire-and-forget meaningful-action logging for analytics. Never throws.
export function trackEvent(type, payload = {}) {
  ensureSession().then((s) =>
    fetch(`${BASE}/api/events`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Client-Id": getClientId(), "X-Session-Id": s.session_id, "X-Session-Token": s.token },
      body: JSON.stringify({ type, payload }),
    }).catch(() => {})
  ).catch(() => {});
}
